import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
export const useModule = () => useContext(Ctx);

const MASTER_KEYS = ["customers", "vendors", "saleitems", "pim", "colourcodes", "costcentre"];
const ALL = ["upcast", "extrusion", "quality", "drawbench", "rmpricing", "toolmgmt", "workorder", "planning", "customers", "vendors", "dispatch", "saleitems", "pim", "colourcodes", "costcentre", "purchase", "fbc", "fbcindent", "crm", "fixedassets", "packing", "kapton", "customersale", "hr"];

// Cost-centre grouping over modules (drives the module switcher + Admin access UI).
export const MODULE_LABELS = {
  upcast: "Upcast", extrusion: "Extrusion", quality: "Quality", drawbench: "Draw Production", toolmgmt: "Tool Mgmt", workorder: "Work Order", planning: "Planning",
  fbc: "FBC Job Work", fbcindent: "FBC RM Indent", rmpricing: "RM Pricing", dispatch: "Dispatch",
  crm: "CRM", purchase: "Purchase", masters: "Masters", fixedassets: "Fixed Assets",
  packing: "Packing", kapton: "Tape Control", customersale: "Sahney", hr: "HR",
};
export const MODULE_GROUPS = [
  { cc: "EXT", label: "EXT", mods: ["workorder", "planning", "upcast", "extrusion", "quality", "packing", "toolmgmt"] },   // drawbench (Draw Production) is reached from the Extrusion sidebar, not the switcher
  { cc: "FBC", label: "FBC", mods: ["fbc", "fbcindent"], entries: [
    { value: "fbc:indent", mod: "fbcindent", label: "RM Indent", to: "/fbc/indent" },
    { value: "fbc:jobwork", mod: "fbc", label: "Job Work", to: "/packing-list" },
  ] },
  { cc: "DISPATCH", label: "Dispatch", mods: ["dispatch"], entries: [
    { value: "dispatch:rdc", mod: "dispatch", label: "RDC", to: "/dispatch/delivery-challans" },
    { value: "dispatch:nrdc", mod: "dispatch", label: "N RDC", to: "/dispatch/non-returnable-dc" },
    { value: "dispatch:invoice", mod: "dispatch", label: "Invoice", to: "/dispatch" },
  ] },
  { cc: "PURCHASE", label: "Purchase", mods: ["purchase"], entries: [
    { value: "purchase:req", mod: "purchase", label: "Requisitions", to: "/purchase/requisitions" },
    { value: "purchase:po", mod: "purchase", label: "Orders", to: "/purchase/orders" },
    { value: "purchase:grn", mod: "purchase", label: "GRN", to: "/purchase/goods-receipt" },
    { value: "purchase:vinv", mod: "purchase", label: "Vendor Inv", to: "/purchase/vendor-invoices" },
  ] },
  { cc: "KAPTON", label: "Kapton", mods: ["kapton"], entries: [
    { value: "kapton:tape", mod: "kapton", label: "Tape Control" },
    { value: "kapton:packing", mod: "kapton", label: "Packing", to: "/kapton/packing/packing-list" },
    { value: "kapton:production", mod: "kapton", label: "Production", to: "/kapton/production/work-orders" },
  ] },
  { cc: "ADMIN", label: "Admin", mods: ["masters", "fixedassets"] },
  { cc: "RM", label: "RM Pricing", mods: ["rmpricing"] },
  { cc: "CRM", label: "CRM / Sales", mods: ["crm"] },
  { cc: "CUSTOMERSALE", label: "Sahney", mods: ["customersale"] },
  { cc: "HR", label: "HR", mods: ["hr"] },
];

export const ModuleProvider = ({ children }) => {
  const { user } = useAuth();

  // Real per-module access (drives page/nav visibility + backend gating).
  const rawAvailable = useMemo(() => {
    if (!user) return [];
    if (user.role === "admin") return ALL;
    return user.modules || [];
  }, [user]);

  // Collapse Customers/Vendors/Sale Items into a single "masters" entry for the switcher.
  const available = useMemo(() => {
    const out = [];
    let mastersAdded = false;
    for (const m of rawAvailable) {
      if (MASTER_KEYS.includes(m)) {
        if (!mastersAdded) { out.push("masters"); mastersAdded = true; }
      } else out.push(m);
    }
    return out;
  }, [rawAvailable]);

  const hasModule = (k) => rawAvailable.includes(k);

  const [module, setModuleState] = useState(() => {
    const stored = localStorage.getItem("module") || "upcast";
    return MASTER_KEYS.includes(stored) ? "masters" : stored;
  });

  useEffect(() => {
    if (available.length && !available.includes(module)) {
      setModuleState(available[0]);
    }
  }, [available, module]);

  const setModule = (m) => {
    const norm = MASTER_KEYS.includes(m) ? "masters" : m;
    setModuleState(norm);
    localStorage.setItem("module", norm);
  };

  return <Ctx.Provider value={{ module, setModule, available, rawAvailable, hasModule }}>{children}</Ctx.Provider>;
};
